// ============================================================
// MASH machine_card viewer — section bodies (ME-MACHINEID-RENDER SP-2)
// ============================================================
// Pure presentational composition of the SP-1a honesty atoms (FigureTile /
// VerdictChip / CoverageValue / Breakdown / CameraWatchBanner). NO honesty logic
// of its own — it consumes the engine resolutions AS-IS (esp. the
// freshness-authority rule, §7A flag #1: freshness comes from the card-level
// FreshnessBanner in the parent; nothing here recomputes it from age).
//
// ME-CARDS-R1 A5: laid out to the locked prototype — section subtitle, big-number
// hero with the companion rate line (explicit denominator), supporting tile row
// beneath. Payload-driven throughout: a section with no rate figure simply has no
// rate line.
import React from "react";

import type {
  EvidenceTab,
  Figure,
  MachineCardArtifactData,
  WindowSelection,
} from "../../../../../../shared/components/machineEvidence";
import {
  Breakdown,
  CameraWatchBanner,
  CoverageValue,
  FigureTile,
  VerdictChip,
  deriveCameraWatch,
  isPlaceholderTab,
  projectFigureToSelection,
  resolveBreakdown,
  resolveCoverage,
  resolveVerdict,
} from "../../../../../../shared/components/machineEvidence";
import useTranslation from "../../../../../../shared/hooks/useTranslation";
import { dt } from "../../../../../../shared/themes/redesignTokens";
import { ARM_ALSO_MEASURED_KEYS, TRAY_UNUSED_SLOTS } from "../machineCardConstants";
import { eyebrow, figureSignalKey, gridStyle, sourceMapOf, subtitleStyle } from "./sectionChrome";
import { getUiLocale } from "../../../../../../shared/utils/relativeTime";

// ───────────────────────────────────────────────────────────────────
// Provisional tray visual — 6-of-8 fallback (GAP-2 / §7 Q3). Muted cells are
// the two known-unused slots; flagged PROVISIONAL until the bot ships the
// authoritative physical position map.
// ───────────────────────────────────────────────────────────────────
export const TrayVisual: React.FC = () => {
  const { translate } = useTranslation("machineEvidence");
  const cells = Array.from({ length: 8 }, (_, i) => `${Math.floor(i / 4) + 1}_${(i % 4) + 1}`);
  return (
    <div className="msc-tray" data-provisional="tray-6of8" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={eyebrow}>
        {translate("tray.title", { defaultValue: "Tray fill (provisional)" }) as string}
      </span>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 28px)", gap: 6 }}>
        {cells.map((slot) => {
          const unused = TRAY_UNUSED_SLOTS.includes(slot);
          return (
            <div
              key={slot}
              title={unused ? (translate("tray.slotUnused", { slot, defaultValue: "{{slot}} — unused" }) as string) : slot}
              style={{
                width: 28,
                height: 28,
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`,
                background: unused ? dt.inputBg : dt.roleGreenBg,
                opacity: unused ? 0.45 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: dt.fontMono,
                fontSize: 9,
                color: unused ? dt.textMuted : dt.roleGreen,
              }}
            >
              {slot}
            </div>
          );
        })}
      </div>
      <span style={{ fontSize: 11, color: dt.textMuted, fontStyle: "italic" }}>
        {translate("tray.provisionalCaption", {
          defaultValue: "provisional 6-of-8 fallback — awaiting bot physical position map (GAP-2)",
        }) as string}
      </span>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────
// Hero — the section's headline number, rendered large with its label (and the
// selected window) beside it, per output-model.PNG / overview-model.PNG.
// ───────────────────────────────────────────────────────────────────
const Hero: React.FC<{ figure: Figure; dim: boolean; windowLabel?: string }> = ({
  figure,
  dim,
  windowLabel,
}) => {
  const coverage = resolveCoverage(figure);
  const breakdown = resolveBreakdown(figure.breakdown);
  return (
    <div className="msc-hero" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 30, lineHeight: 1.1, fontWeight: 700 }}>
          <CoverageValue resolution={coverage} unit={figure.unit} denominator={figure.denominator} dim={dim} />
        </span>
        <span style={{ fontSize: 13, color: dt.textSecondary }}>
          {figure.label}
          {windowLabel ? ` · ${windowLabel}` : ""}
        </span>
      </div>
      {breakdown ? <Breakdown resolution={breakdown} /> : null}
    </div>
  );
};

/** The companion rate line: value + label + the EXPLICIT denominator pair
 *  ("4.76% reject rate · 267 / 5,607"). Rendered only for a figure that actually
 *  carries a denominator — a share is never shown without its base. */
const RateLine: React.FC<{ figure: Figure; dim: boolean }> = ({ figure, dim }) => {
  const coverage = resolveCoverage(figure);
  return (
    <div
      className="msc-hero-rate"
      style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}
    >
      <span style={{ fontSize: 16, fontWeight: 600 }}>
        <CoverageValue resolution={coverage} unit={figure.unit} dim={dim} />
      </span>
      <span style={{ fontSize: 13, color: dt.textSecondary }}>{figure.label}</span>
      {coverage.showsValue && figure.denominator ? (
        <span style={{ fontFamily: dt.fontMono, fontSize: 12, color: dt.textMuted }}>
          {figure.denominator.num.toLocaleString(getUiLocale())} / {figure.denominator.den.toLocaleString(getUiLocale())}
        </span>
      ) : null}
    </div>
  );
};

export const FiguresGrid: React.FC<{
  figures: Figure[];
  card: MachineCardArtifactData;
  selection: WindowSelection;
}> = ({ figures, card, selection }) => {
  const sources = sourceMapOf(card);
  if (figures.length === 0) return null;
  return (
    <div style={gridStyle}>
      {figures.map((f) => {
        const projected = projectFigureToSelection(f, selection);
        return (
          <FigureTile
            key={f.id}
            figure={projected}
            cardFreshness={card.freshness}
            signalKey={figureSignalKey(projected, card, sources)}
            showBreakdown
          />
        );
      })}
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────
// Arm — Picker + Jaws MERGED into ONE tab, rendered as TWO GROUPS (PICKING +
// SIZE). SIZE = the residual-band figure (the ONLY figure carrying a
// `threshold`, plus any jaw/residual/size id) — everything else is PICKING.
// GAP-6 "also measured, not shown yet" slot = signal-key labels only.
// ───────────────────────────────────────────────────────────────────
const isSizeFigure = (f: Figure): boolean =>
  f.threshold != null || /jaw|residual|size/i.test(`${f.id} ${f.label}`);

export const ArmGroups: React.FC<{
  tab: EvidenceTab;
  card: MachineCardArtifactData;
  selection: WindowSelection;
}> = ({ tab, card, selection }) => {
  const { translate } = useTranslation("machineEvidence");
  const figures = tab.figures ?? [];
  const sizeFigs = figures.filter(isSizeFigure);
  const pickingFigs = figures.filter((f) => !isSizeFigure(f));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={eyebrow}>{translate("arm.picking", { defaultValue: "Picking" }) as string}</span>
        {pickingFigs.length > 0 ? (
          <FiguresGrid figures={pickingFigs} card={card} selection={selection} />
        ) : (
          <span style={{ fontSize: 12, color: dt.textMuted, fontStyle: "italic" }}>
            {translate("arm.noPickingFigures", { defaultValue: "— no picking figures this window" }) as string}
          </span>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={eyebrow}>{translate("arm.size", { defaultValue: "Size" }) as string}</span>
        {sizeFigs.length > 0 ? (
          <FiguresGrid figures={sizeFigs} card={card} selection={selection} />
        ) : (
          <span style={{ fontSize: 12, color: dt.textMuted, fontStyle: "italic" }}>
            {translate("arm.noSizeFigures", { defaultValue: "— no size figures this window" }) as string}
          </span>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={eyebrow}>
            {translate("arm.alsoMeasured", { defaultValue: "Also measured, not shown yet" }) as string}
          </span>
          <span style={{ fontFamily: dt.fontMono, fontSize: 11, color: dt.textMuted }}>
            {ARM_ALSO_MEASURED_KEYS.join(" · ")}
          </span>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────
// Section body — subtitle + verdict + hero (+ rate line) + supporting tiles +
// section-specific chrome. Placeholder tabs render "Pending — not wired this
// milestone", NEVER a 0/fault.
// ───────────────────────────────────────────────────────────────────
export const SectionBody: React.FC<{
  tab: EvidenceTab;
  card: MachineCardArtifactData;
  dim: boolean;
  selection: WindowSelection;
  /** label of the currently selected window, shown beside the hero. */
  windowLabel?: string;
}> = ({ tab, card, dim, selection, windowLabel }) => {
  const { translate } = useTranslation("machineEvidence");
  const verdict = resolveVerdict(tab.verdict);
  const figures = tab.figures ?? [];

  if (isPlaceholderTab(tab)) {
    return (
      <div className="msc-section-body msc-section-placeholder" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <VerdictChip resolution={verdict} chipOnly />
        <span data-placeholder style={{ fontSize: 13, color: dt.textMuted, fontStyle: "italic" }}>
          {/* Payload headline renders as-is; only the client FALLBACK is translated. */}
          {verdict.headlineIsFallback
            ? (translate("section.pendingPlaceholder", {
                defaultValue: "Pending — not wired this milestone",
              }) as string)
            : verdict.headline}
        </span>
      </div>
    );
  }

  // A5: the one-line section descriptor from the locked design. Client chrome,
  // keyed per section slug — an unknown slug simply has none.
  const subtitle = translate(`section.subtitle.${tab.part_slug}`, { defaultValue: "" }) as string;

  // A5 hero split: the first figure is the hero; the FIRST figure carrying an
  // explicit denominator becomes the companion rate line; the rest are tiles.
  const [hero, ...rest] = figures;
  const rateIndex = rest.findIndex((f) => !!f.denominator);
  const rate = rateIndex >= 0 ? rest[rateIndex] : null;
  const supporting = rateIndex >= 0 ? rest.filter((_, i) => i !== rateIndex) : rest;

  return (
    <div className="msc-section-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {subtitle ? (
        <span className="msc-section-subtitle" style={subtitleStyle}>
          {subtitle}
        </span>
      ) : null}
      <VerdictChip resolution={verdict} />
      {tab.part_slug === "arm" ? (
        <ArmGroups tab={tab} card={card} selection={selection} />
      ) : (
        <>
          {hero ? (
            <Hero
              figure={projectFigureToSelection(hero, selection)}
              dim={dim}
              windowLabel={windowLabel}
            />
          ) : null}
          {rate ? <RateLine figure={projectFigureToSelection(rate, selection)} dim={dim} /> : null}
          <FiguresGrid figures={supporting} card={card} selection={selection} />
        </>
      )}
      {tab.part_slug === "unloader" ? <TrayVisual /> : null}
      {tab.part_slug === "vision" ? <CameraWatchBanner show={deriveCameraWatch(tab, card)} /> : null}
    </div>
  );
};

export default SectionBody;
